import React, { useEffect, useMemo, useRef, useState } from 'react';
import { AnimatePresence, motion } from 'framer-motion';
import { Dices, Globe, Monitor, PlugZap, User } from 'lucide-react';
import { io, type Socket } from 'socket.io-client';
import './assets/bootstrap/bootstrap.css';
import './assets/font-awesome/css/fontawesome.css';
import './App.css';
import EngineGame from './ui/EngineGame';
import type { Action, GameState } from './engine';
import { OnlineApiConfig, clearAllGames, createGame, createOrResumeGuest, fetchGameState, fetchLatestAiProfile, joinGame, lookupGameByCode, type ApiMode } from './onlineApi';
import SetupScreen, { type SetupDifficulty, type SetupMode, type SetupPlayers } from './components/SetupScreen';
import TableScreen from './components/TableScreen';
import QuitGameModal from './components/QuitGameModal';
import ConfirmModal from './components/ConfirmModal';
import { syncTrainingData } from './training/syncTraining';
import { setCommunityAiProfile } from './ai/aiLearning';
import ProfileScreen, { type ProfileChoice } from './components/ProfileScreen';
import { clearActiveProfileState, createUserProfile, getActiveUserProfileId, listUserProfiles, setActiveUserProfileId } from './profiles/userProfiles';
import { resetLocalGameData } from './storage/resetLocalGameData';

const GUEST_TOKEN_KEY = 'omulongo_guest_token';
const GUEST_NAME_KEY = 'omulongo_guest_name';
const LAST_INVITE_CODE_KEY = 'omulongo_last_invite_code';
const LAST_SEAT_KEY = 'omulongo_last_seat';

type PlayMode = 'LOCAL' | 'ONLINE';
type AppScreen = 'PROFILE' | 'SETUP' | 'PLAY';

function modeToApi(mode: GameState['mode']): ApiMode {
  return mode === '4p' ? 'FOUR_PLAYER' : 'TWO_PLAYER';
}

function viewerIdFromSeat(seat?: number): string {
  if (!seat) return 'P1';
  return `P${seat}`;
}

function inferViewerIdFromRedactedState(state: GameState | null): string | null {
  if (!state) return null;
  for (const [playerId, cards] of Object.entries(state.hands || {})) {
    if (!Array.isArray(cards)) continue;
    if (cards.some((c: any) => typeof c?.id === 'string' && !c.id.startsWith('HIDDEN-'))) return playerId;
  }
  return null;
}

function App(): JSX.Element {
  const [screen, setScreen] = useState<AppScreen>(() => {
    try {
      const profiles = listUserProfiles();
      const active = getActiveUserProfileId();
      return profiles.length && active ? 'SETUP' : 'PROFILE';
    } catch {
      return 'PROFILE';
    }
  });
  const [quitOpen, setQuitOpen] = useState<boolean>(false);
  const [setupPlayers, setSetupPlayers] = useState<SetupPlayers>('2p');
  const [setupDifficulty, setSetupDifficulty] = useState<SetupDifficulty>('NORMAL');
  const [setupMode, setSetupMode] = useState<SetupMode>('LOCAL');
  const [setupVsComputer, setSetupVsComputer] = useState<boolean>(false);
  const [setupTeamMode, setSetupTeamMode] = useState<boolean>(false);
  const [localMode, setLocalMode] = useState<GameState['mode']>('2p');
  const [localSessionId, setLocalSessionId] = useState<number>(0);

  const [playMode, setPlayMode] = useState<PlayMode>('LOCAL');
  const [guestToken, setGuestToken] = useState<string>('');
  const [displayName, setDisplayName] = useState<string>(localStorage.getItem(GUEST_NAME_KEY) || '');
  const [onlineState, setOnlineState] = useState<GameState | null>(null);
  const [gameId, setGameId] = useState<string>('');
  const [inviteCode, setInviteCode] = useState<string>('');
  const [yourSeat, setYourSeat] = useState<number | null>(null);
  const [yourTeam, setYourTeam] = useState<number | null>(null);
  const [gameStatus, setGameStatus] = useState<'WAITING' | 'PLAYING' | 'ENDED' | ''>('');

  const [createMode, setCreateMode] = useState<GameState['mode']>('2p');
  const [joinCode, setJoinCode] = useState<string>('');
  const [lookupMode, setLookupMode] = useState<ApiMode | null>(null);
  const [takenSeats, setTakenSeats] = useState<number[]>([]);
  const [joinSeat, setJoinSeat] = useState<number>(2);
  const [statusMessage, setStatusMessage] = useState<string>('');
  const [errorMessage, setErrorMessage] = useState<string>('');
  const [isBusy, setIsBusy] = useState<boolean>(false);
  const [clearGamesOpen, setClearGamesOpen] = useState<boolean>(false);

  const socketRef = useRef<Socket | null>(null);
  const lastOnlineStateAtRef = useRef<number>(0);
  const onlineSyncInFlightRef = useRef<boolean>(false);

  const effectiveViewerPlayerId = useMemo(() => {
    return inferViewerIdFromRedactedState(onlineState) || viewerIdFromSeat(yourSeat || undefined);
  }, [onlineState, yourSeat]);

  useEffect(() => {
    if (playMode !== 'ONLINE') return;

    const init = async (): Promise<void> => {
      try {
        setIsBusy(true);
        setErrorMessage('');
        const existingToken = localStorage.getItem(GUEST_TOKEN_KEY) || undefined;
        const fallbackName = (localStorage.getItem(GUEST_NAME_KEY) || 'Guest').trim();
        const guest = await createOrResumeGuest(fallbackName, existingToken);
        localStorage.setItem(GUEST_TOKEN_KEY, guest.guestToken);
        localStorage.setItem(GUEST_NAME_KEY, guest.displayName);
        setGuestToken(guest.guestToken);
        setDisplayName(guest.displayName);
      } catch (error) {
        setErrorMessage(error instanceof Error ? error.message : 'Failed to initialize guest account.');
      } finally {
        setIsBusy(false);
      }
    };

    init();
  }, [playMode]);

  useEffect(() => {
    if (playMode !== 'ONLINE') return;
    if (!guestToken) return;
    syncTrainingData(guestToken).catch(() => {
      // training sync is best-effort
    });
  }, [playMode, guestToken]);

  useEffect(() => {
    if (playMode !== 'ONLINE') return;
    if (!guestToken) return;
    fetchLatestAiProfile(guestToken)
      .then((res) => setCommunityAiProfile(res.profile))
      .catch(() => {
        // best-effort; local profile still applies
      });
  }, [playMode, guestToken]);

  useEffect(() => {
    if (playMode !== 'ONLINE' || !guestToken) return;

    const socket = io(OnlineApiConfig.socketBase, {
      path: '/socket.io',
      transports: ['websocket', 'polling'],
      auth: { guestToken },
    });

    socket.on('connect', () => {
      setStatusMessage((prev) => (prev ? prev : 'Connected to online server.'));
      if (inviteCode) {
        socket.emit('join_game', { inviteCode, seat: yourSeat || undefined, guestToken });
      }
    });

    socket.on('state', (payload: { gameId: string; state: GameState; status: 'WAITING' | 'PLAYING' | 'ENDED'; yourSeat: number; yourTeam: number }) => {
      lastOnlineStateAtRef.current = Date.now();
      setGameId(payload.gameId);
      setOnlineState(payload.state);
      setGameStatus(payload.status);
      setYourSeat(payload.yourSeat);
      setYourTeam(payload.yourTeam);
      setErrorMessage('');
    });

    socket.on('error', (payload: { message?: string }) => {
      setErrorMessage(payload?.message || 'Socket error');
    });

    socket.on('app_error', (payload: { message?: string }) => {
      setErrorMessage(payload?.message || 'Socket error');
    });

    socket.on('disconnect', () => {
      setStatusMessage('Disconnected from online server. Reconnecting...');
    });

    socketRef.current = socket;
    return () => {
      socket.removeAllListeners();
      socket.disconnect();
      socketRef.current = null;
    };
  }, [playMode, guestToken, inviteCode, yourSeat]);

  useEffect(() => {
    if (playMode !== 'ONLINE') return;
    if (!guestToken) return;
    if (!gameId) return;

    let cancelled = false;

    const syncNow = async (): Promise<void> => {
      if (onlineSyncInFlightRef.current) return;
      onlineSyncInFlightRef.current = true;
      try {
        const res = await fetchGameState(guestToken, gameId);
        if (cancelled) return;
        lastOnlineStateAtRef.current = Date.now();
        setOnlineState(res.state);
        setGameStatus(res.status);
        setYourSeat(res.yourSeat);
        setYourTeam(res.yourTeam);
        setErrorMessage('');
      } catch (error) {
        if (cancelled) return;
        const socketConnected = Boolean(socketRef.current?.connected);
        if (!socketConnected) {
          setErrorMessage(error instanceof Error ? error.message : 'Failed to sync online game.');
        }
      } finally {
        onlineSyncInFlightRef.current = false;
      }
    };

    const maybeSync = (): void => {
      const socketConnected = Boolean(socketRef.current?.connected);
      const staleMs = Date.now() - (lastOnlineStateAtRef.current || 0);
      const isStale = staleMs > 4000;
      if (!socketConnected || isStale) syncNow().catch(() => {});
    };

    const interval = window.setInterval(maybeSync, 1500);
    const onVisibilityChange = (): void => {
      if (document.visibilityState === 'visible') maybeSync();
    };
    document.addEventListener('visibilitychange', onVisibilityChange);

    // Kick once on mount (helps after returning from background).
    maybeSync();

    return () => {
      cancelled = true;
      window.clearInterval(interval);
      document.removeEventListener('visibilitychange', onVisibilityChange);
    };
  }, [playMode, guestToken, gameId]);

  const waitingMessage = useMemo(() => {
    if (!onlineState || !yourSeat) return '';
    const viewerId = effectiveViewerPlayerId;
    if (onlineState.currentPlayerId !== viewerId) {
      return `${onlineState.currentPlayerId} is thinking...`;
    }
    return "It's your turn.";
  }, [onlineState, yourSeat, effectiveViewerPlayerId]);

  const onCreateOnlineGame = async (): Promise<void> => {
    if (!guestToken) return;
    try {
      setIsBusy(true);
      setErrorMessage('');
      const created = await createGame(guestToken, modeToApi(createMode));
      setGameId(created.gameId);
      setInviteCode(created.inviteCode);
      setYourSeat(created.seat);
      setYourTeam(created.seat === 1 || created.seat === 3 ? 1 : 2);
      setOnlineState(created.state);
      lastOnlineStateAtRef.current = Date.now();
      setGameStatus('WAITING');
      setStatusMessage(`Game created. Invite code: ${created.inviteCode}`);
      localStorage.setItem(LAST_INVITE_CODE_KEY, created.inviteCode);
      localStorage.setItem(LAST_SEAT_KEY, String(created.seat));
      socketRef.current?.emit('join_game', { inviteCode: created.inviteCode, seat: created.seat, guestToken });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create game.');
    } finally {
      setIsBusy(false);
    }
  };

  const onRestartOnlineGame = async (): Promise<void> => {
    if (!guestToken) return;
    if (socketRef.current && gameId) {
      setIsBusy(true);
      setErrorMessage('');
      socketRef.current.emit('restart_game', { gameId }, (res: { ok: boolean; message?: string }) => {
        if (!res?.ok) setErrorMessage(res?.message || 'Failed to restart game.');
        else setStatusMessage('Restarting game…');
        setIsBusy(false);
      });
      return;
    }
    try {
      setIsBusy(true);
      setErrorMessage('');
      const mode = onlineState?.mode || createMode;
      const created = await createGame(guestToken, modeToApi(mode));
      setGameId(created.gameId);
      setInviteCode(created.inviteCode);
      setYourSeat(created.seat);
      setYourTeam(created.seat === 1 || created.seat === 3 ? 1 : 2);
      setOnlineState(created.state);
      lastOnlineStateAtRef.current = Date.now();
      setGameStatus('WAITING');
      setStatusMessage(`New game created. Invite code: ${created.inviteCode}`);
      localStorage.setItem(LAST_INVITE_CODE_KEY, created.inviteCode);
      localStorage.setItem(LAST_SEAT_KEY, String(created.seat));
      socketRef.current?.emit('join_game', { inviteCode: created.inviteCode, seat: created.seat, guestToken });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to create game.');
    } finally {
      setIsBusy(false);
    }
  };

  const onLookupGame = async (): Promise<void> => {
    if (!guestToken || !joinCode.trim()) return;
    try {
      setIsBusy(true);
      setErrorMessage('');
      const lookup = await lookupGameByCode(guestToken, joinCode.trim().toUpperCase());
      setLookupMode(lookup.mode);
      setTakenSeats(lookup.takenSeats);
      if (lookup.mode === 'TWO_PLAYER') {
        setJoinSeat(2);
      }
      setStatusMessage(`Game found (${lookup.mode}). Taken seats: ${lookup.takenSeats.join(', ') || 'none'}`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to lookup game.');
    } finally {
      setIsBusy(false);
    }
  };

  const onJoinOnlineGame = async (codeOverride?: string, seatOverride?: number): Promise<void> => {
    if (!guestToken) return;
    const rawCode = codeOverride || joinCode;
    if (!rawCode.trim()) return;

    try {
      setIsBusy(true);
      setErrorMessage('');
      const normalizedCode = rawCode.trim().toUpperCase();
      const seat = typeof seatOverride === 'number' ? seatOverride : (lookupMode === 'FOUR_PLAYER' ? joinSeat : undefined);
      const joined = await joinGame(guestToken, normalizedCode, seat);
      setGameId(joined.gameId);
      setInviteCode(normalizedCode);
      setYourSeat(joined.seat);
      setYourTeam(joined.team);
      setOnlineState(joined.state);
      lastOnlineStateAtRef.current = Date.now();
      setGameStatus(joined.status);
      setStatusMessage(`Joined as P${joined.seat}.`);
      localStorage.setItem(LAST_INVITE_CODE_KEY, normalizedCode);
      localStorage.setItem(LAST_SEAT_KEY, String(joined.seat));
      socketRef.current?.emit('join_game', { inviteCode: normalizedCode, seat: joined.seat, guestToken });
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to join game.');
    } finally {
      setIsBusy(false);
    }
  };

  const onReconnectLastGame = async (): Promise<void> => {
    const lastCode = localStorage.getItem(LAST_INVITE_CODE_KEY);
    const lastSeat = localStorage.getItem(LAST_SEAT_KEY);
    if (!lastCode) return;
    await onJoinOnlineGame(lastCode, lastSeat ? Number(lastSeat) : undefined);
  };

  const onClearAllGames = async (): Promise<void> => {
    if (!guestToken) return;
    try {
      setIsBusy(true);
      setErrorMessage('');
      const res = await clearAllGames(guestToken);
      localStorage.removeItem(LAST_INVITE_CODE_KEY);
      localStorage.removeItem(LAST_SEAT_KEY);
      setOnlineState(null);
      setGameId('');
      setInviteCode('');
      setYourSeat(null);
      setYourTeam(null);
      setLookupMode(null);
      setTakenSeats([]);
      setStatusMessage(`Cleared games (${res.deletedGames}).`);
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to clear games.');
    } finally {
      setIsBusy(false);
      setClearGamesOpen(false);
    }
  };

  const onUpdateDisplayName = async (): Promise<void> => {
    if (!guestToken) return;
    try {
      setIsBusy(true);
      setErrorMessage('');
      const updated = await createOrResumeGuest(displayName, guestToken);
      setDisplayName(updated.displayName);
      localStorage.setItem(GUEST_NAME_KEY, updated.displayName);
      setStatusMessage('Display name updated.');
    } catch (error) {
      setErrorMessage(error instanceof Error ? error.message : 'Failed to update display name.');
    } finally {
      setIsBusy(false);
    }
  };

  const onDispatchOnlineAction = (action: Action): void => {
    if (!gameId) return;
    const expectedPlayerId = effectiveViewerPlayerId;
    const move =
      action && typeof action === 'object' && 'playerId' in action
        ? ({ ...(action as any), playerId: expectedPlayerId } as Action)
        : action;
    socketRef.current?.emit('move', { gameId, move }, (res: { ok: boolean; message?: string }) => {
      if (res?.ok) return;
      setErrorMessage(res?.message || 'Invalid move');
      // Immediately resync (helps when a client is slightly out-of-date).
      fetchGameState(guestToken, gameId)
        .then((fresh) => {
          lastOnlineStateAtRef.current = Date.now();
          setOnlineState(fresh.state);
          setGameStatus(fresh.status);
          setYourSeat(fresh.yourSeat);
          setYourTeam(fresh.yourTeam);
        })
        .catch(() => {});
    });
  };

  const seatOptions = useMemo(() => {
    const options = lookupMode === 'FOUR_PLAYER' ? [1, 2, 3, 4] : [2];
    return options.filter((seat) => !takenSeats.includes(seat));
  }, [lookupMode, takenSeats]);

  const hasReconnect = Boolean(localStorage.getItem(LAST_INVITE_CODE_KEY));

  const onSetupPlayersChange = (value: SetupPlayers): void => {
    setSetupPlayers(value);
    if (value !== '2p') setSetupVsComputer(false);
  };

  const onSetupModeChange = (value: SetupMode): void => {
    setSetupMode(value);
    if (value !== 'LOCAL') setSetupVsComputer(false);
  };

  const onStartFromSetup = (): void => {
    setPlayMode(setupMode);
    const resolvedMode: GameState['mode'] = setupPlayers === '4p' ? '4p' : '2p';
    setLocalMode(resolvedMode);
    setCreateMode(resolvedMode);
    if (setupMode !== 'LOCAL' || setupPlayers !== '2p') {
      setSetupVsComputer(false);
    }
    setLocalSessionId((prev) => prev + 1);
    setScreen('PLAY');
  };

  const resetOnlineState = (): void => {
    setPlayMode('LOCAL');
    setOnlineState(null);
    setGameId('');
    setInviteCode('');
    setYourSeat(null);
    setYourTeam(null);
    setLookupMode(null);
    setTakenSeats([]);
    setStatusMessage('');
    setErrorMessage('');
  };

  const onQuitToProfile = (): void => {
    setQuitOpen(false);
    resetOnlineState();
    resetLocalGameData().catch(() => {});
    setLocalSessionId((prev) => prev + 1);
    setScreen('PROFILE');
  };

  const onNewGameFromMenu = (): void => {
    clearActiveProfileState();
    resetOnlineState();
    setLocalSessionId((prev) => prev + 1);
    setScreen('SETUP');
  };

  const onProfileContinue = (choice: ProfileChoice): void => {
    if (choice.type === 'create') {
      createUserProfile(choice.name);
    } else {
      setActiveUserProfileId(choice.id);
    }
    setScreen('SETUP');
  };

  return (
    <div className="App">
      <AnimatePresence mode="wait">
        {screen === 'PROFILE' ? (
          <motion.div key="profile" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <ProfileScreen
              profiles={listUserProfiles().map((p) => ({ id: p.id, name: p.name }))}
              activeProfileId={getActiveUserProfileId()}
              onContinue={onProfileContinue}
            />
          </motion.div>
        ) : screen === 'SETUP' ? (
          <motion.div key="setup" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <SetupScreen
              players={setupPlayers}
              difficulty={setupDifficulty}
              mode={setupMode}
              vsComputer={setupVsComputer}
              teamMode={setupTeamMode}
              onPlayersChange={onSetupPlayersChange}
              onDifficultyChange={setSetupDifficulty}
              onModeChange={onSetupModeChange}
              onVsComputerChange={setSetupVsComputer}
              onTeamModeChange={setSetupTeamMode}
              onStart={onStartFromSetup}
            />
          </motion.div>
        ) : (
          <motion.div key="play" initial={{ opacity: 0 }} animate={{ opacity: 1 }} exit={{ opacity: 0 }}>
            <TableScreen>
              {playMode === 'LOCAL' ? (
                <EngineGame
                  key={`local-${localMode}-${localSessionId}`}
                  playMode={playMode}
                  onPlayModeChange={setPlayMode}
                  lockedMode={localMode}
                  viewerPlayerId={setupVsComputer && localMode === '2p' ? 'P1' : undefined}
                  aiEnabled={setupVsComputer && localMode === '2p'}
                  aiPlayerIds={setupVsComputer && localMode === '2p' ? ['P2'] : undefined}
                  difficulty={setupDifficulty}
                  onQuitRequest={() => setQuitOpen(true)}
                  onNewGameRequest={onNewGameFromMenu}
                />
              ) : null}

              {playMode === 'ONLINE' ? (
                <>
          {!onlineState ? (
            <>
              <nav className="navbar navbar-dark engine-nav px-3 mb-2">
                <div className="d-flex justify-content-between align-items-center w-100 flex-wrap gap-2">
                  <div className="engine-logo">
                    <Dices size={16} className="me-2" aria-hidden="true" />
                    <span>omu</span>
                    <span className="diamond">1♦</span>
                    <span>ngo</span>
                  </div>
                  <div className="d-flex gap-2 align-items-center flex-wrap">
                    <button className="mode-pill" onClick={() => setPlayMode('LOCAL')} type="button">
                      <Monitor size={14} aria-hidden="true" />
                      <span>Local</span>
                    </button>
                    <button className="mode-pill active online" onClick={() => setPlayMode('ONLINE')} type="button">
                      <Globe size={14} aria-hidden="true" />
                      <span>Online</span>
                    </button>
                    <span className="badge text-bg-dark mode-guest-badge">
                      <User size={13} className="me-1" aria-hidden="true" />
                      <span>{displayName || 'Guest'}</span>
                    </span>
                    {hasReconnect ? (
                      <button className="btn btn-sm btn-outline-light" onClick={onReconnectLastGame} type="button">
                        <PlugZap size={13} className="me-1" aria-hidden="true" />
                        Reconnect
                      </button>
                    ) : null}
                    <button
                      className="btn btn-sm btn-outline-danger"
                      type="button"
                      disabled={isBusy || !guestToken}
                      onClick={() => setClearGamesOpen(true)}
                      title="Delete all online games from the server"
                    >
                      Clear Games
                    </button>
                  </div>
                </div>
              </nav>

              <div className="online-lobby card p-3">
                <h4 className="mb-2">Online Lobby</h4>
                {errorMessage ? <div className="alert alert-danger py-2">{errorMessage}</div> : null}
                {statusMessage ? <div className="alert alert-secondary py-2">{statusMessage}</div> : null}

                <div className="mb-3">
                  <label className="form-label">Display Name</label>
                  <div className="d-flex gap-2">
                    <input
                      className="form-control"
                      value={displayName}
                      onChange={(e) => {
                        setDisplayName(e.target.value);
                        localStorage.setItem(GUEST_NAME_KEY, e.target.value);
                      }}
                    />
                    <button type="button" className="btn btn-outline-light" disabled={isBusy || !guestToken || !displayName.trim()} onClick={onUpdateDisplayName}>
                      Update Name
                    </button>
                  </div>
                </div>

                <div className="row g-3">
                  <div className="col-md-6">
                    <h5>Create Game</h5>
                    <select className="form-select mb-2" value={createMode} onChange={(e) => setCreateMode(e.target.value as GameState['mode'])}>
                      <option value="2p">2 Players (P1 creates, P2 joins)</option>
                      <option value="4p">4 Players (P1+P3 vs P2+P4)</option>
                    </select>
                    <button disabled={isBusy || !guestToken} className="btn btn-primary" onClick={onCreateOnlineGame}>Create Online Game</button>
                  </div>

                  <div className="col-md-6">
                    <h5>Join Game</h5>
                    <input className="form-control mb-2" placeholder="Invite code" value={joinCode} onChange={(e) => setJoinCode(e.target.value.toUpperCase())} />
                    <div className="d-flex gap-2 mb-2">
                      <button disabled={isBusy || !guestToken || !joinCode} className="btn btn-outline-primary" onClick={onLookupGame}>Check Game</button>
                      <button disabled={isBusy || !guestToken || !joinCode || !lookupMode} className="btn btn-success" onClick={() => onJoinOnlineGame()}>Join</button>
                    </div>

                    {lookupMode ? (
                      <>
                        <div className="small mb-1">Mode: {lookupMode}</div>
                        <div className="small mb-2">4p teams: P1 + P3 and P2 + P4</div>
                        {lookupMode === 'FOUR_PLAYER' ? (
                          <select className="form-select" value={joinSeat} onChange={(e) => setJoinSeat(Number(e.target.value))}>
                            {seatOptions.map((seat) => (
                              <option key={seat} value={seat}>
                                Join as P{seat}
                              </option>
                            ))}
                          </select>
                        ) : (
                          <div className="small">2p open game join seat: P2</div>
                        )}
                      </>
                    ) : null}
                  </div>
                </div>
              </div>
            </>
          ) : (
            <>
              {waitingMessage ? <div className="thinking-float">{waitingMessage}</div> : null}
              {errorMessage ? <div className="alert alert-danger py-2">{errorMessage}</div> : null}

              <EngineGame
                stateOverride={onlineState}
                onDispatchAction={onDispatchOnlineAction}
                viewerPlayerId={effectiveViewerPlayerId}
                lockedMode={onlineState.mode}
                playMode={playMode}
                onPlayModeChange={setPlayMode}
                inviteCode={inviteCode}
                onlineSeatLabel={yourSeat ? `P${yourSeat}` : ''}
                onlineTeamLabel={yourTeam ? String(yourTeam) : ''}
                onlineStatusLabel={gameStatus || ''}
                onNewGameRequest={onRestartOnlineGame}
                onQuitRequest={() => {
                  setOnlineState(null);
                  setInviteCode('');
                  setGameId('');
                  setYourSeat(null);
                  setYourTeam(null);
                  setLookupMode(null);
                  setTakenSeats([]);
                  setStatusMessage('Left online game.');
                }}
                onLeaveOnline={() => {
                  setOnlineState(null);
                  setInviteCode('');
                  setGameId('');
                  setYourSeat(null);
                  setYourTeam(null);
                  setLookupMode(null);
                  setTakenSeats([]);
                  setStatusMessage('Left online game.');
                }}
                showReconnect={hasReconnect}
                onReconnect={onReconnectLastGame}
                difficulty={setupDifficulty}
              />
            </>
          )}
        </>
      ) : null}
            </TableScreen>
          </motion.div>
        )}
      </AnimatePresence>

      <QuitGameModal open={quitOpen} onCancel={() => setQuitOpen(false)} onConfirm={onQuitToProfile} />
      <ConfirmModal
        open={clearGamesOpen}
        title="Clear all online games?"
        message="This deletes all online games from the server for everyone on this network."
        confirmLabel="Clear Games"
        cancelLabel="Cancel"
        confirmVariant="danger"
        onCancel={() => setClearGamesOpen(false)}
        onConfirm={onClearAllGames}
      />
    </div>
  );
}

export default App;
